import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  MoreHoriz as MoreIcon
} from '@mui/icons-material';
import { groupService } from '../../services/group.service';
import { ContestList } from '../contests/ContestList';
import { ContestForm } from '../contests/ContestForm';
import { GroupMembers } from './GroupMembers';
import { GroupMembersPreview } from './GroupMembersPreview';
import { GroupForm } from './GroupForm';
import { JoinRequestButton } from './JoinRequestButton';
import { apiService } from '../../services/api.service';
import { BreadcrumbTrail } from '../ui/BreadcrumbTrail';
import { InitialsAvatar } from '../ui/InitialsAvatar';
import { StatusPill } from '../ui/StatusPill';
import { SurfaceCard } from '../ui/SurfaceCard';
import { EmptyState } from '../ui/EmptyState';
import i18n from '../../i18n/i18n-setup';
import { formatDate } from '../../i18n/locale-format';

interface Group {
  _id: string;
  name: string;
  description: string;
  private: boolean;
  memberCount: number;
  createdBy: string;
  createdAt: string;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

type ContestPayload = Parameters<typeof apiService.createContest>[0];

export const GroupDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('groups');
  const [searchParams, setSearchParams] = useSearchParams();
  const [group, setGroup] = useState<Group | null>(null);
  const [contestDialogOpen, setContestDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [contestsKey, setContestsKey] = useState(0); // For refreshing contest list
  const [membersKey, setMembersKey] = useState(0); // For refreshing members preview
  const [userRole, setUserRole] = useState<'admin' | 'member' | null>(null);
  const [roleResolved, setRoleResolved] = useState(false);
  const [currentUserId] = useState<string>(''); // Not available from auth context yet
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });

  const showMessage = useCallback((message: string, severity: SnackbarState['severity']) => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const fetchGroup = useCallback(async () => {
    if (!id) return;
    try {
      const response = await groupService.getGroup(id);
      setGroup(response.data);
    } catch (error) {
      console.error('Error fetching group:', error);
      // i18n.t keeps this callback stable across language switches, so it does not refetch.
      showMessage(i18n.t('groups:detail.messages.loadFailed'), 'error');
    }
  }, [id, showMessage]);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setRoleResolved(false);
      await fetchGroup();
      try {
        const roleResponse = await groupService.getUserRole(id);
        setUserRole(roleResponse.data.role);
      } catch (roleError) {
        // User might not be a member - that's okay
        setUserRole(null);
      } finally {
        setRoleResolved(true);
      }
    };
    load();
  }, [id, fetchGroup]);

  // Deep link from the global "New challenge" picker: open the form for admins,
  // otherwise explain why not. The param is dropped so a refresh does not repeat it.
  // The group-id check matters when navigating group A -> group B: this component is reused,
  // so group/userRole/roleResolved still hold A's values on the first render for B.
  const wantsNewChallenge = searchParams.get('newChallenge') === '1';
  useEffect(() => {
    if (!wantsNewChallenge || !roleResolved || !group || group._id !== id) return;
    if (userRole === 'admin') {
      setContestDialogOpen(true);
    } else {
      showMessage(i18n.t('groups:detail.messages.adminOnlyChallenges'), 'warning');
    }
    const next = new URLSearchParams(searchParams);
    next.delete('newChallenge');
    setSearchParams(next, { replace: true });
  }, [wantsNewChallenge, roleResolved, group, id, userRole, searchParams, setSearchParams, showMessage]);

  const handleSaveContest = async (contestData: ContestPayload) => {
    try {
      setLoading(true);
      await apiService.createContest(contestData);
      setContestDialogOpen(false);
      setContestsKey(prev => prev + 1); // Trigger contest list refresh
      showMessage(t('detail.messages.contestCreated'), 'success');
    } catch (error) {
      console.error('Error creating contest:', error);
      showMessage(t('detail.messages.contestCreateFailed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGroup = async (data: { name: string; description: string; private: boolean }) => {
    if (!id) return;
    try {
      await groupService.updateGroup(id, data);
      setEditDialogOpen(false);
      await fetchGroup();
      showMessage(t('detail.messages.groupUpdated'), 'success');
    } catch (error) {
      console.error('Error updating group:', error);
      showMessage(t('detail.messages.groupUpdateFailed'), 'error');
    }
  };

  const handleDeleteGroup = async () => {
    setMenuAnchor(null);
    if (!id || !window.confirm(t('detail.deleteConfirm'))) return;
    try {
      await groupService.deleteGroup(id);
      navigate('/groups');
    } catch (error) {
      console.error('Error deleting group:', error);
      showMessage(t('detail.messages.groupDeleteFailed'), 'error');
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const closeMembersDialog = () => {
    setMembersDialogOpen(false);
    setMembersKey(prev => prev + 1);
    fetchGroup(); // member count may have changed
  };

  const isAdmin = userRole === 'admin';
  const canViewDetails = !group?.private || userRole !== null; // Can view details if public group or is member

  if (!group) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const onJoinRequestSent = () => showMessage(t('detail.messages.joinRequestSent'), 'success');

  return (
    <>
      <BreadcrumbTrail items={[{ label: t('detail.breadcrumb'), to: '/groups' }, { label: group.name }]} />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <InitialsAvatar name={group.name} size={56} shape="rounded" gradient />
        <Box sx={{ flex: 1, minWidth: 220, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box component="h1" sx={{ m: 0, fontSize: 24, fontWeight: 600, letterSpacing: '-0.02em', overflowWrap: 'anywhere' }}>
              {group.name}
            </Box>
            {userRole && <StatusPill tone="outline" label={t(`roles.${userRole}`)} />}
          </Box>
          <Box sx={{ fontSize: 14, color: 'var(--muted)' }}>
            {[
              group.description,
              t('detail.members', { count: group.memberCount }),
              group.private ? t('visibility.private') : t('visibility.public'),
              t('detail.created', { date: formatDate(group.createdAt) })
            ]
              .filter(Boolean)
              .join(' · ')}
          </Box>
        </Box>
        {isAdmin && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setContestDialogOpen(true)}>
              {t('detail.newChallenge')}
            </Button>
            <IconButton
              aria-label={t('detail.actionsAria')}
              onClick={e => setMenuAnchor(e.currentTarget)}
              sx={{ border: '1px solid var(--border)', borderRadius: '8px', backgroundColor: 'var(--card)' }}
            >
              <MoreIcon />
            </IconButton>
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  setEditDialogOpen(true);
                }}
              >
                <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                {t('detail.editGroup')}
              </MenuItem>
              <MenuItem onClick={handleDeleteGroup} sx={{ color: 'var(--danger)' }}>
                <ListItemIcon sx={{ color: 'inherit' }}><DeleteIcon fontSize="small" /></ListItemIcon>
                {t('detail.deleteGroup')}
              </MenuItem>
            </Menu>
          </Box>
        )}
        {!userRole && canViewDetails && (
          <Box sx={{ minWidth: 160 }}>
            <JoinRequestButton groupId={group._id} userRole={userRole} isPrivate={group.private} onJoinRequestSent={onJoinRequestSent} />
          </Box>
        )}
      </Box>

      {canViewDetails ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2, alignItems: 'start' }}>
          <Box sx={{ minWidth: 0 }}>
            <ContestList key={contestsKey} groupId={group._id} />
          </Box>
          <GroupMembersPreview
            groupId={group._id}
            isAdmin={isAdmin}
            totalMembers={group.memberCount}
            refreshKey={membersKey}
            onViewAll={() => setMembersDialogOpen(true)}
          />
        </Box>
      ) : (
        <SurfaceCard bodyPadding={0}>
          <EmptyState
            icon={<LockIcon />}
            title={t('detail.privateTitle')}
            description={t('detail.privateDescription')}
            action={<JoinRequestButton groupId={group._id} userRole={userRole} isPrivate={group.private} onJoinRequestSent={onJoinRequestSent} />}
          />
        </SurfaceCard>
      )}

      <Dialog open={contestDialogOpen} onClose={() => setContestDialogOpen(false)} maxWidth="sm" fullWidth>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <ContestForm groupId={group._id} onSave={handleSaveContest} onCancel={() => setContestDialogOpen(false)} />
        )}
      </Dialog>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} fullWidth maxWidth="xs">
        <GroupForm group={group} onSave={handleSaveGroup} onCancel={() => setEditDialogOpen(false)} />
      </Dialog>

      <Dialog open={membersDialogOpen} onClose={closeMembersDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {t('detail.membersDialogTitle')}
          <IconButton aria-label={t('common:actions.close')} onClick={closeMembersDialog} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <GroupMembers groupId={group._id} userRole={userRole} currentUserId={currentUserId} />
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
